import React, { useEffect, useState } from 'react';
import './MyEngagement.scss';
import {
    LevelsOfService,
    LevelOfService,
} from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { LegalEntityTypeList } from '../../../slices/marketplace/timeline/models/enums/legal-enity-type';
import { stateToCodeMapper } from '../../../utils/locationsHelper';
import { Button } from 'reactstrap';
import { SuspendEngagementPopup } from './SuspendEngagementPopup/SuspendEngagementPopup';
import {
    EngagementProcessStatus,
    EngagementProcessStatuses,
} from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { TerminateEngagementPopup } from './TerminateEngagementPopup/TerminateEngagementPopup';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import EditEngagementValuesModal from './EditEngagementValuesModal/EditEngagementValuesModal';
import { MyEngagementCommercialTerms } from './MyEngagementCommercialTerms/MyEngagementCommercialTerms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { useDispatch } from 'react-redux';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import { UpdateUseCreditsForMonthlyFees } from '../../../slices/marketplace/timeline/thunks/update-use-credits-for-monthly-fees.thunk';

interface MyEngagementProps {
    fetchData(): void;
    isAdminView: boolean;
    isReadOnly?: boolean;
    isRestrictedAccess?: boolean;
    party?: TimelineParty;
}

const MyEngagement = (props: MyEngagementProps) => {
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const [
        isSuspendEngagementPopupVisible,
        setIsSuspendEngagementPopupVisible,
    ] = useState(false);
    const [
        isTerminateEngagementPopupVisible,
        setIsTerminateEngagementPopupVisible,
    ] = useState(false);
    const [isResumeEngagementPopupVisible, setIsResumeEngagementPopupVisible] =
        useState(false);
    const [isResumeEngagementInProcess, setIsResumeEngagementInProcess] =
        useState(false);
    const [isEditValuesModalVisible, setIsEditValuesModalVisible] = useState(false);
    const [isSavingValues, setIsSavingValues] = useState(false);
    const [isUpdatingCreditsSetting, setIsUpdatingCreditsSetting] =
        useState(false);
    const [pendingCreditsSettingValue, setPendingCreditsSettingValue] =
        useState<boolean | null>(null);
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);
    const [notificationFlagCode, setNotificationFlagCode] = useState<string>();
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const formattedDate = new Date(
        currentEngagement.engagementProcess.startDate
    ).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
    });

    const party = useCurrentMarketplaceParty(currentEngagement?.id);
    const isPartyBroker = (props.party ?? party) === TimelineParty.Broker;

    useEffect(() => {
        const notificationCode = `${NotificationFlagsCategories.MpCode}.${currentEngagement.engagementProcess.timelineId}.${NotificationFlagsCategories.EngagementCode}.${NotificationFlagsCategories.EngagementStatusCode}`;
        setNotificationFlagCode(notificationCode);
    }, [currentEngagement.engagementProcess]);

    const columns = [
        {
            dataField: 'practiceName',
            text: 'Practice Name',
            sort: false,
            headerClasses: 'practice-name-header',
        },
        {
            dataField: 'entityName',
            text: 'Legal Entity Name',
            sort: false,
            headerClasses: 'practice-name-header',
        },
        {
            dataField: 'entityType',
            headerClasses: 'entity-type-header',
            text: 'Legal Entity Type',
            sort: false,
            formatter: (cellContent: number) => {
                return (
                    LegalEntityTypeList.find((item) => item.id === cellContent)
                        ?.abbreviation ?? 'NA'
                );
            },
        },
        {
            dataField: 'stateOfRegistration',
            headerClasses: 'state-of-registration-header',
            text: 'Legal Entity State of Registration',
            sort: false,
            formatter: (cellContent: string) => {
                return stateToCodeMapper(cellContent) ?? 'NA';
            },
        },
    ];

    const getPracticesTable = () => {
        return (
            <BootstrapTable
                bootstrap4
                remote
                keyField="id"
                data={currentEngagement.engagementProcess.practices}
                columns={columns}
                noDataIndication="No data available"
            />
        );
    };

    const resumeEngagement = () => {
        setIsResumeEngagementInProcess(true);
        axios
            .post(
                `/api/Marketplace/Engagements/${currentEngagement.engagementProcess.timelineId}/Resume`
            )
            .then((response) => {
                if (response?.request?.status !== 200) {
                    createNotification(
                        'An error occured during resuming engagement',
                        'error'
                    );
                } else {
                    setIsResumeEngagementPopupVisible(false);
                    props.fetchData();
                }
            })
            .catch(() => {
                createNotification(
                    'An error occured during resuming engagement',
                    'error'
                );
            })
            .finally(() => {
                setIsResumeEngagementInProcess(false);
                props.fetchData();
            });
    };

    const handleSaveEngagementValues = (fteDvm: number, ltmRevenue: number, reason: string) => {
        setIsSavingValues(true);
        axios
            .put(
                `/api/Marketplace/Engagements/${currentEngagement.engagementProcess.timelineId}/startFteDvmAndLtmRevenue`,
                {
                    startFteDvm: fteDvm,
                    startLtmRevenue: ltmRevenue,
                    reason: reason,
                }
            )
            .then((response) => {
                if (response?.status === 200) {
                    createNotification(
                        'Engagement values updated successfully',
                        'success'
                    );
                    setIsEditValuesModalVisible(false);
                    props.fetchData();
                } else {
                    createNotification(
                        'An error occurred while updating engagement values',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'An error occurred while updating engagement values',
                    'error'
                );
            })
            .finally(() => {
                setIsSavingValues(false);
            });
    };

    const confirmUseCreditsForMonthlyFeesChange = () => {
        if (pendingCreditsSettingValue === null || isUpdatingCreditsSetting) {
            return;
        }
        setIsUpdatingCreditsSetting(true);
        dispatch(
            UpdateUseCreditsForMonthlyFees({
                engagementId: currentEngagement.engagementProcess.timelineId,
                useCreditsForMonthlyFees: pendingCreditsSettingValue,
            })
        ).finally(() => {
            setIsUpdatingCreditsSetting(false);
            setPendingCreditsSettingValue(null);
        });
    };

    const getUseCreditsSetting = () => {
        // SFAS engagements incur neither monthly engagement fees nor suspension fees,
        // so the credits setting has no effect there — don't show it.
        if (
            currentEngagement.levelOfService ===
            LevelOfService.StrategicFinancialAdvisoryService
        ) {
            return null;
        }

        const engagementStatus =
            currentEngagement.engagementProcess.status;
        // Editable while the engagement is Current or Suspended (suspension fees
        // are charged during suspension, so the setting must stay togglable then —
        // same states in which the Resume/Suspend actions are offered). Terminated
        // has no future fees; genuine read-only viewers stay locked out.
        const isEngagementActiveOrSuspended =
            engagementStatus === EngagementProcessStatus.Current ||
            engagementStatus === EngagementProcessStatus.Suspended;

        return (
            <div className="use-credits-setting">
                <label className="use-credits-label">
                    <input
                        type="checkbox"
                        checked={
                            currentEngagement.engagementProcess
                                .useCreditsForMonthlyFees || false
                        }
                        disabled={
                            !isEngagementActiveOrSuspended ||
                            props.isRestrictedAccess ||
                            isPartyBroker ||
                            isUpdatingCreditsSetting
                        }
                        onChange={(e) =>
                            setPendingCreditsSettingValue(e.target.checked)
                        }
                    />
                    Use wallet credits for monthly fees
                </label>
                <InfoTooltip
                    text="When enabled, available wallet credits will be applied to monthly engagement and suspension fees before your saved payment method is charged. You can change this setting at any time."
                    idText="use-credits-for-monthly-fees"
                />
            </div>
        );
    };

    const getButtonsSection = () => {
        return (
            <div className="buttons-section">
                {currentEngagement.engagementProcess.status ===
                    EngagementProcessStatus.Suspended &&
                !props.isRestrictedAccess &&
                !props.isAdminView &&
                !isPartyBroker ? (
                    <Button
                        color="primary"
                        className="select-documents-btn"
                        onClick={() => {
                            setIsResumeEngagementPopupVisible(true);
                        }}>
                        Resume Engagement
                    </Button>
                ) : currentEngagement.engagementProcess.status ===
                      EngagementProcessStatus.Current &&
                  !props.isReadOnly &&
                  !props.isAdminView &&
                  !isPartyBroker ? (
                    <Button
                        color="primary"
                        className="select-documents-btn"
                        onClick={() => {
                            setIsSuspendEngagementPopupVisible(true);
                        }}>
                        Suspend Engagement
                    </Button>
                ) : (
                    <></>
                )}
                {currentEngagement.engagementProcess.status !==
                    EngagementProcessStatus.Terminated &&
                    !props.isReadOnly &&
                    !props.isAdminView &&
                    !isPartyBroker && (
                        <Button
                            color="primary"
                            className="select-documents-btn"
                            onClick={() => {
                                setIsTerminateEngagementPopupVisible(true);
                            }}>
                            Terminate Engagement
                        </Button>
                    )}
            </div>
        );
    };

    return (
        <div className="my-engagement">
            <div className="body_inner_content">
                <div className="obpbq_row valuation_section">
                    <div className="sampledetail">
                        <h6>
                            Ownership Representative:{' '}
                            <span>
                                {currentEngagement.engagementProcess.userName}
                            </span>
                        </h6>
                        <h6>
                            Level of Service:{' '}
                            <span>
                                {
                                    LevelsOfService.find(
                                        (l) =>
                                            l.id ===
                                            currentEngagement.levelOfService
                                    )?.name
                                }
                            </span>
                        </h6>
                        <h6>
                            Engagement Start Date: <span>{formattedDate}</span>
                        </h6>
                        <h6>
                            FTE DVMs, at engagement start:{' '}
                            <span>
                                {currentEngagement.engagementProcess.startFteDvm}
                            </span>
                            {props.isAdminView && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 ml-2"
                                    onClick={() => setIsEditValuesModalVisible(true)}
                                    style={{ verticalAlign: 'baseline' }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                            )}
                        </h6>
                        <h6>
                            LTM Revenue, at engagement start:{' '}
                            <span>
                                ${currentEngagement.engagementProcess.startLtmRevenue.toLocaleString()}
                            </span>
                            {props.isAdminView && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 ml-2"
                                    onClick={() => setIsEditValuesModalVisible(true)}
                                    style={{ verticalAlign: 'baseline' }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>
                            )}
                        </h6>
                        <div className="engagement-status-row">
                            <h6>
                                Engagement Status:{' '}
                                <span>
                                    {
                                        EngagementProcessStatuses.find(
                                            (x) =>
                                                x.id ===
                                                currentEngagement
                                                    .engagementProcess.status
                                        ).name
                                    }
                                </span>
                            </h6>
                            <NotificationFlag
                                eventCode={notificationFlagCode}
                                includePrefix={true}/>
                        </div>
                        <h6>Practices:</h6>
                        {getPracticesTable()}
                        {getUseCreditsSetting()}
                        {getButtonsSection()}
                        <MyEngagementCommercialTerms
                            levelOfService={currentEngagement.levelOfService}
                            startFteDvm={currentEngagement.engagementProcess.startFteDvm || 0}
                            startLtmRevenue={currentEngagement.engagementProcess.startLtmRevenue || 0}
                            marketplacePricing={marketplacePricing}
                        />
                    </div>
                </div>
            </div>
            {isSuspendEngagementPopupVisible && (
                <SuspendEngagementPopup
                    engagementId={
                        currentEngagement.engagementProcess.timelineId
                    }
                    fteDvmNumber={currentEngagement.engagementProcess.startFteDvm || 0}
                    startLtmRevenue={currentEngagement.engagementProcess.startLtmRevenue || 0}
                    levelOfService={currentEngagement.levelOfService}
                    isVisible={isSuspendEngagementPopupVisible}
                    setIsVisible={setIsSuspendEngagementPopupVisible}
                    fetchData={props.fetchData}
                />
            )}
            {isTerminateEngagementPopupVisible && (
                <TerminateEngagementPopup
                    engagementId={
                        currentEngagement.engagementProcess.timelineId
                    }
                    levelOfService={currentEngagement.levelOfService}
                    isVisible={isTerminateEngagementPopupVisible}
                    setIsVisible={setIsTerminateEngagementPopupVisible}
                    fetchData={props.fetchData}
                />
            )}
            {pendingCreditsSettingValue !== null && (
                <ConfirmationModal
                    header={'Confirm Credits Setting Change'}
                    isVisible={pendingCreditsSettingValue !== null}
                    setIsVisible={(visible: boolean) => {
                        if (!visible) {
                            setPendingCreditsSettingValue(null);
                        }
                    }}
                    confirmationText={
                        pendingCreditsSettingValue
                            ? 'Are you sure you want to use available wallet credits for monthly engagement and suspension fees? Credits will be applied before your saved payment method is charged.'
                            : 'Are you sure you want to stop using wallet credits for monthly fees? Your saved payment method will be charged in full.'
                    }
                    params={''}
                    onConfirm={confirmUseCreditsForMonthlyFeesChange}
                    nextButtonText={
                        pendingCreditsSettingValue ? 'Enable' : 'Disable'
                    }
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isUpdatingCreditsSetting}
                />
            )}
            {isResumeEngagementPopupVisible && (
                <ConfirmationModal
                    header={'Confirm Resuming Engagement'}
                    isVisible={isResumeEngagementPopupVisible}
                    setIsVisible={setIsResumeEngagementPopupVisible}
                    confirmationText="Are you sure you want to resume the engagement?"
                    params={''}
                    onConfirm={() => {
                        if (!isResumeEngagementInProcess) {
                            resumeEngagement();
                        }
                    }}
                    nextButtonText="Resume"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isResumeEngagementInProcess}
                />
            )}
            {props.isAdminView && isEditValuesModalVisible && (
                <EditEngagementValuesModal
                    engagementId={currentEngagement.engagementProcess.timelineId}
                    currentFteDvm={currentEngagement.engagementProcess.startFteDvm}
                    currentLtmRevenue={currentEngagement.engagementProcess.startLtmRevenue}
                    isVisible={isEditValuesModalVisible}
                    setIsVisible={setIsEditValuesModalVisible}
                    onSave={handleSaveEngagementValues}
                    isSaving={isSavingValues}
                />
            )}
        </div>
    );
};

export default MyEngagement;